import { immutable as arrayUnique } from 'array-unique';

import * as api from '@gitbook/api-client';
import { validateContentReference } from '@gitbook/app-content-refs';
import type { Change } from '@gitbook/slate';

import type {
    BlockTableData,
    TableCardsView,
    TableColumnDefinition,
    TableDefinitionImageRecord,
    TableDefinitions,
    TableRecord,
    TableRecords,
    TableRecordValue,
} from '../../types';
import { insertColumn, setColumnDefinition, setRecordValue, setView } from '../changes';
import { createColumnFromType } from '../utils';
import type { ViewValidationSpec } from './types';

/** Validation for a cards view */
export const cardsViewSpec: ViewValidationSpec<TableCardsView> = {
    normalize: (view, table) => {
        let result = view;
        const colDefs = Object.keys(table.definition);

        if (typeof result.hideColumnTitle !== 'boolean') {
            result = {
                ...result,
                hideColumnTitle: false,
            };
        }

        if (!result.cardSize || !['medium', 'large'].includes(result.cardSize)) {
            result = {
                ...result,
                cardSize: 'medium',
            };
        }

        // At least one column is required if there is no cover definition
        if (!Array.isArray(result.columns) || result.columns.length === 0) {
            // If there is no cover value, we keep the non-cover columns
            if (!!result.coverDefinition && !hasCoverValue(table.records, result.coverDefinition)) {
                result = {
                    ...result,
                    columns: colDefs.filter(
                        (key) => ![result.coverDefinition, result.coverDefinitionDark].includes(key)
                    ),
                };
            }
        } else {
            // Column orders should have unique columns
            const unique = arrayUnique(result.columns).filter((key) => colDefs.includes(key));
            if (unique.length !== result.columns.length) {
                result = {
                    ...result,
                    columns: unique,
                };
            }
        }

        if (
            result.coverDefinition &&
            !validateCoverDefinition(result.coverDefinition, table.definition)
        ) {
            result = {
                ...result,
                coverDefinition: undefined,
                coverDefinitionDark: undefined,
            };
        }

        // If the coverDefinition is not set, we remove the coverDefinitionDark
        if (
            result.coverDefinitionDark &&
            (!result.coverDefinition ||
                !validateCoverDefinition(result.coverDefinitionDark, table.definition))
        ) {
            result = {
                ...result,
                coverDefinitionDark: undefined,
            };
        }

        // The targetDefinition should be a valid content-ref definition
        if (
            typeof result.targetDefinition !== 'undefined' &&
            (typeof result.targetDefinition !== 'string' ||
                table.definition[result.targetDefinition]?.type !== 'content-ref')
        ) {
            result = {
                ...result,
                targetDefinition: undefined,
            };
        }

        return result as TableCardsView;
    },
};

function hasCoverValue(records: TableRecords, coverDefinition: string) {
    return Object.values(records).some((record) => {
        const coverValue = record.values[coverDefinition];
        if (!coverValue) {
            return false;
        }

        // Handle new format with ref and objectFit
        if (isExpandedImageRecordFormat(coverValue)) {
            return validateRef(coverValue.ref);
        }

        // Handle legacy format (direct ContentRef)
        return validateRef(coverValue);
    });
}

/**
 * Validate the cover definition. It should be a string and a column definition that is either a files or image column.
 */
function validateCoverDefinition(coverDefinition: string, definitions: TableDefinitions): boolean {
    if (typeof coverDefinition !== 'string' || !definitions[coverDefinition]) {
        return false;
    }

    if (!['files', 'image'].includes(definitions[coverDefinition].type)) {
        return false;
    }

    return true;
}

/**
 * Update the cover image for a record. It creates if a new column definition if none is specified in the view.
 */
export function setCardCoverForRecord(
    change: Change,
    args: {
        tableKey: string;
        recordId: string;
        coverRef: api.ContentRefFile | api.ContentRefURL;
        /**
         * light: only set the light cover image.
         * dark: only set the dark cover image. Should throw an exception if a light cover image is not set.
         * both: set both light and dark cover images. This is equivalent to setting the light and removing the dark ref.
         */
        mode?: 'light' | 'dark' | 'both';
    }
) {
    const { tableKey, recordId, coverRef, mode = 'light' } = args;

    const tableData = change.value.document
        .assertBlock(tableKey)
        .data.toFrozenObject() as BlockTableData;

    if (tableData.view.type !== 'cards') {
        throw new Error('Cannot set cover for a non-cards view');
    }

    // Prevent setting dark cover without a light cover definition
    if (mode === 'dark' && !tableData.view.coverDefinition) {
        throw new Error('Cannot set dark cover without a light cover definition');
    }

    // Get the applied cover definition for the current theme
    let appliedCoverDefinition = (() => {
        if (mode === 'dark') {
            return tableData.view.coverDefinitionDark
                ? tableData.definition[tableData.view.coverDefinitionDark]
                : undefined;
        }

        return tableData.view.coverDefinition
            ? tableData.definition[tableData.view.coverDefinition]
            : undefined;
    })();

    // If the column doesn't exist already, create it.
    if (!appliedCoverDefinition || !['files', 'image'].includes(appliedCoverDefinition.type)) {
        const title = mode === 'dark' ? 'Cover image (dark)' : 'Cover image';
        appliedCoverDefinition = createColumnFromType('image', title);
        insertColumn(change, tableKey, appliedCoverDefinition);
        setView(change, tableKey, {
            ...tableData.view,
            // Set the cover definition for the current theme
            // Make sure to use coverDefinitionDark only if coverDefinition is set
            [mode === 'dark' ? 'coverDefinitionDark' : 'coverDefinition']:
                appliedCoverDefinition.id,
        });
    }

    // If we are setting both, we remove the coverDefinitionDark
    if (mode === 'both' && tableData.view.coverDefinitionDark) {
        setView(change, tableKey, {
            ...tableData.view,
            coverDefinitionDark: undefined,
        });
    }

    const coverDefinitionColumnId = tableData.view.coverDefinition;
    const coverDefinitionColumn = coverDefinitionColumnId
        ? tableData.definition[coverDefinitionColumnId]
        : undefined;

    // Cover definitions used to use the "files" column type. Now they use the "image" type. Convert the old "files" to "image".
    if (
        coverDefinitionColumnId &&
        coverDefinitionColumn &&
        coverDefinitionColumn.type === 'files'
    ) {
        migrateCoverValueToImageFormat(
            change,
            tableKey,
            coverDefinitionColumnId,
            coverDefinitionColumn,
            tableData.records
        );
    }

    setRecordValue(change, tableKey, recordId, appliedCoverDefinition.id, coverRef);
}

const validateRef = validateContentReference(['file', 'url']);

/**
 * Get the cover for a record.
 */
export function getRecordCover(props: {
    coverDefinition?: string;
    coverDefinitionDark?: string;
    definition: TableDefinitions;
    record: TableRecord;
    mode: 'light' | 'dark';
}): {
    coverRef: api.ContentRefFile | api.ContentRefURL;
    objectFit: api.CardsImageObjectFit;
    alt?: string;
} | null {
    const { coverDefinition, coverDefinitionDark, definition, record, mode } = props;

    if (!coverDefinition && !coverDefinitionDark) {
        return null;
    }

    // Get the cover definition for the current theme
    const themedCoverDefinition =
        coverDefinitionDark && mode === 'dark' ? coverDefinitionDark : coverDefinition;

    if (!themedCoverDefinition) {
        return null;
    }

    const coverColumnDef = definition[themedCoverDefinition];
    const coverValue = record.values[themedCoverDefinition];

    // If we are on dark mode and there is no cover value, we return the light cover value
    if (mode === 'dark' && !coverValue) {
        return getRecordCover({
            ...props,
            mode: 'light',
        });
    }

    if (!coverColumnDef || coverValue == null) {
        return null;
    }

    // Handle legacy files column (string)
    if (typeof coverValue === 'string') {
        return {
            coverRef: { kind: 'file' as const, file: coverValue },
            objectFit: api.CardsImageObjectFit.Cover,
            alt: undefined,
        };
    }

    // Handle array values
    if (Array.isArray(coverValue) && coverValue.length > 0) {
        const firstValue = coverValue[0];

        // Files column: array of strings (file IDs)
        if (typeof firstValue === 'string') {
            return {
                coverRef: { kind: 'file' as const, file: firstValue },
                objectFit: api.CardsImageObjectFit.Cover,
                alt: undefined,
            };
        }
    }

    // Handle new format with ref and objectFit FIRST
    if (coverValue && typeof coverValue === 'object' && 'ref' in coverValue) {
        const typedValue = coverValue;

        if (validateRef(typedValue.ref)) {
            return {
                coverRef: typedValue.ref,
                objectFit: getObjectFitFromImageValue(typedValue),
                alt: getAltFromImageValue(typedValue),
            };
        }
    }

    // Handle single content reference (legacy format)
    if (validateRef(coverValue)) {
        return {
            coverRef: coverValue,
            objectFit: api.CardsImageObjectFit.Cover,
            alt: undefined,
        };
    }

    return null;
}

/**
 * Remove the cover image for a record.
 */
export function removeCardCoverForRecord(
    change: Change,
    args: {
        tableKey: string;
        recordId: string;
    }
) {
    const { tableKey, recordId } = args;

    const tableData = change.value.document
        .assertBlock(tableKey)
        .data.toFrozenObject() as BlockTableData;

    if (tableData.view.type !== 'cards') {
        throw new Error('Cannot set cover for a non-cards view');
    }

    if (!tableData.view.coverDefinition && !tableData.view.coverDefinitionDark) {
        return;
    }

    if (tableData.view.coverDefinition) {
        setRecordValue(change, tableKey, recordId, tableData.view.coverDefinition, null);
    }

    if (tableData.view.coverDefinitionDark) {
        setRecordValue(change, tableKey, recordId, tableData.view.coverDefinitionDark, null);
    }
}

/**
 * Helper function to check if a value is in the new format with ref and objectFit
 */
export function isExpandedImageRecordFormat(value: TableRecordValue): value is {
    ref: api.ContentRefFile | api.ContentRefURL;
    objectFit?: api.CardsImageObjectFit;
    alt?: string;
} {
    return value !== null && typeof value === 'object' && 'ref' in value;
}

/**
 * Helper function to get the actual ContentRef from either format
 */
export function getContentRefFromImageValue(
    value: TableRecordValue
): api.ContentRefFile | api.ContentRefURL {
    return isExpandedImageRecordFormat(value)
        ? value.ref
        : (value as api.ContentRefFile | api.ContentRefURL);
}

/**
 * Helper function to get the alt text from either format
 */
function getAltFromImageValue(value: TableRecordValue): string | undefined {
    return isExpandedImageRecordFormat(value) ? value.alt : undefined;
}

/**
 * Helper function to get objectFit from either format
 */
function getObjectFitFromImageValue(value: TableRecordValue): api.CardsImageObjectFit {
    if (isExpandedImageRecordFormat(value) && value.objectFit) {
        return value.objectFit;
    }
    return api.CardsImageObjectFit.Cover;
}

export function setCardCoverAttributesForRecord(
    change: Change,
    args: {
        tableKey: string;
        recordId: string;
        mode: 'light' | 'dark';
        value: {
            objectFit?: api.CardsImageObjectFit;
            altText?: string;
        };
    }
): void {
    const { tableKey, recordId, mode, value } = args;

    let tableData = change.value.document
        .assertBlock(tableKey)
        .data.toFrozenObject() as BlockTableData;

    if (tableData.view.type !== 'cards') {
        throw new Error('Cannot set cover attributes for a non-cards view');
    }

    const { objectFit, altText } = value;

    if (
        typeof objectFit !== 'undefined' &&
        !Object.values(api.CardsImageObjectFit).includes(objectFit)
    ) {
        throw new Error('Invalid cover object fit. Must be contain, fill, or cover.');
    }

    // Nothing to update
    if (typeof objectFit === 'undefined' && typeof altText === 'undefined') {
        return;
    }

    // Get the cover definition for the current theme
    const themedCoverDefinition =
        tableData.view.coverDefinitionDark && mode === 'dark'
            ? tableData.view.coverDefinitionDark
            : tableData.view.coverDefinition;

    if (!themedCoverDefinition) {
        return;
    }

    tableData = migrateAllLegacyFilesColumns(change, tableKey, tableData);

    const currentValue = tableData.records[recordId]?.values[themedCoverDefinition];
    if (!currentValue) {
        return;
    }

    const newObjectFit =
        typeof objectFit === 'undefined' ? getObjectFitFromImageValue(currentValue) : objectFit;
    const newAlt = typeof altText === 'undefined' ? getAltFromImageValue(currentValue) : altText;

    const updatedValue: TableDefinitionImageRecord = {
        ref: getContentRefFromImageValue(currentValue),
        // If provided, override; otherwise keep existing explicit value if present
        objectFit: newObjectFit,
        alt: newAlt,
    };

    setRecordValue(change, tableKey, recordId, themedCoverDefinition, updatedValue);
}

/**
 * Update the cover image for a record. It creates if a new column definition if none is specified in the view.
 */
export function setCardTargetForRecord(
    change: Change,
    args: {
        tableKey: string;
        recordId: string;
        targetRef: api.ContentRef;
    }
) {
    const { tableKey, recordId, targetRef } = args;

    const tableData = change.value.document
        .assertBlock(tableKey)
        .data.toFrozenObject() as BlockTableData;

    if (tableData.view.type !== 'cards') {
        throw new Error('Cannot set cover for a non-cards view');
    }

    let appliedTargetDefinition = tableData.view.targetDefinition
        ? tableData.definition[tableData.view.targetDefinition]
        : undefined;

    if (!appliedTargetDefinition || appliedTargetDefinition.type !== 'content-ref') {
        appliedTargetDefinition = createColumnFromType('content-ref');
        insertColumn(change, tableKey, appliedTargetDefinition);
        setView(change, tableKey, {
            ...tableData.view,
            targetDefinition: appliedTargetDefinition.id,
        });
    }

    setRecordValue(change, tableKey, recordId, appliedTargetDefinition.id, targetRef);
}

/**
 * Remove the target for the card associated with a record.
 */
export function removeCardTargetForRecord(change: Change, tableKey: string, recordId: string) {
    const tableData = change.value.document
        .assertBlock(tableKey)
        .data.toFrozenObject() as BlockTableData;

    if (tableData.view.type !== 'cards') {
        throw new Error('Cannot set cover for a non-cards view');
    }

    if (!tableData.view.targetDefinition) {
        return;
    }

    setRecordValue(change, tableKey, recordId, tableData.view.targetDefinition, null);
}

/**
 * Migrate any legacy 'files' columns in the provided table data to the 'image' type
 * and return the refreshed `tableData` after potential normalization.
 */
function migrateAllLegacyFilesColumns(
    change: Change,
    tableKey: string,
    tableData: BlockTableData
): BlockTableData {
    const definitions = Object.entries(tableData.definition);

    if (!definitions.some(([_, columnDef]) => columnDef.type === 'files')) {
        return tableData;
    }

    let hasMigrated = false;
    definitions.forEach(([columnId, columnDef]) => {
        if (columnDef.type === 'files') {
            migrateCoverValueToImageFormat(
                change,
                tableKey,
                columnId,
                columnDef,
                tableData.records
            );
            hasMigrated = true;
        }
    });

    if (!hasMigrated) {
        return tableData;
    }

    // Re-read the value after a migration to ensure we work on the normalized format
    return change.value.document.assertBlock(tableKey).data.toFrozenObject() as BlockTableData;
}

function migrateCoverValueToImageFormat(
    change: Change,
    tableKey: string,
    coverDefinitionColumnId: string,
    coverDefinitionColumn: TableColumnDefinition,
    records: TableRecords
) {
    setColumnDefinition(
        change,
        tableKey,
        coverDefinitionColumnId,
        createColumnFromType('image', coverDefinitionColumn.title || 'Cover image'),
        { normalize: false }
    );

    Object.keys(records).forEach((recordId) => {
        const previousValue = records[recordId].values[coverDefinitionColumnId];

        const newValue = (() => {
            if (!previousValue) {
                return null;
            }

            return {
                kind: 'file',
                // file column type is either an array of files or a single file
                file: Array.isArray(previousValue) ? previousValue[0] : previousValue,
            };
        })();

        setRecordValue(change, tableKey, recordId, coverDefinitionColumnId, newValue, {
            normalize: false,
        });
    });

    // Save all normalization until the end of the migration
    change.normalize();
}
